/**
 * Build-time feature flags (Vite env). Each flag gates BOTH the sidebar nav
 * item and its route, so a disabled section is fully unreachable — a direct
 * URL falls through to the catch-all redirect, not the hidden page.
 *
 * Most flags DEFAULT ON: an unset or empty env var means the section shows. To
 * hide a section in an environment, set its `VITE_FEATURE_*` to "false" in that
 * environment's build config. For the real go-live, drop the override (or set
 * it to "true") and the section returns with no code change.
 *
 * Knowledge Base is the exception: it DEFAULTS OFF and only appears when
 * `VITE_FEATURE_KB` is explicitly "true". This hides both the sidebar nav item
 * and the dashboard "Search knowledge base" quick action everywhere until KB is
 * deliberately turned on.
 *
 * Hidden for the end-customer testing go-live (2026-06): SLA Dashboard,
 * Knowledge Base, Trust & Security.
 *
 * Note: delegation is NOT a build-time flag — it is gated server-side
 * (`approval_delegation_enabled`) and surfaced on `/api/me` so one backend
 * toggle drives both the engine and the UI without drift.
 */
function flagEnabled(raw: string | undefined): boolean {
  if (raw === undefined) return true;
  const v = raw.trim().toLowerCase();
  if (v === "") return true;
  return v !== "false" && v !== "0" && v !== "off";
}

/** Like `flagEnabled` but DEFAULTS OFF: only an explicit "true"/"1"/"on" enables it. */
function flagEnabledDefaultOff(raw: string | undefined): boolean {
  if (raw === undefined) return false;
  const v = raw.trim().toLowerCase();
  return v === "true" || v === "1" || v === "on";
}

export const features = {
  sla: flagEnabled(import.meta.env.VITE_FEATURE_SLA),
  kb: flagEnabledDefaultOff(import.meta.env.VITE_FEATURE_KB),
  trust: flagEnabled(import.meta.env.VITE_FEATURE_TRUST),
  // Reporting engine (BrightGauge replacement). DEFAULTS OFF (like KB): the
  // backend ships dormant to prod, and this hides the nav + route everywhere
  // until VITE_FEATURE_REPORTING is explicitly "true" (the staging build sets it;
  // prod leaves it false). Server-side it is additionally gated by
  // reporting_enabled + the view_reports permission, surfaced as me.may_view_reports.
  reporting: flagEnabledDefaultOff(import.meta.env.VITE_FEATURE_REPORTING),
} as const;

export type FeatureKey = keyof typeof features;
